import { Directive, HostBinding, HostListener, input } from '@angular/core';
import { fieldConstraints, fieldPatterns, patternParts, TypeByField, TypeByRule } from 'src/app/models/input-restrict-config';

/*
  ***README***
  hi how are ya

  [appInputRestrict]="'name'" ---> predefined rules according to single input passed (field name)
  [appInputRestrict]="['alphanumeric', 'apostrophe']" ---> flexible version: rules according to array input passed (rule names)

  refer to input-restrict-config.ts for existing rules or to add more rules
*/

@Directive({
  selector: '[appInputRestrict]',
  standalone: true
})
export class InputRestrictDirective {
  appInputRestrict = input<TypeByField | TypeByRule[]>('unknown');

  private get rules(): TypeByRule[] {
    const input = this.appInputRestrict();
    if (Array.isArray(input)) return input;
    if (input === 'unknown') return []; // matches everything
    return fieldPatterns[input];
  }

  private get pattern(): RegExp {
    if (this.appInputRestrict() === 'unknown') return /.*/;
    if (this.rules.length === 0) return /.*/;
    const combined = this.rules.map((r) => patternParts[r].pattern).join('');
    return new RegExp(`^[${combined}]$`, 'u');
  }

  private get consecutivePattern(): RegExp | null {
    const consecutiveChars = this.rules
      .filter((r) => patternParts[r].noConsecutive)
      .map((r) => patternParts[r].pattern)
      .join('');
    if (!consecutiveChars) return null;
    return new RegExp(`[${consecutiveChars}]{2,}`, 'gu');
  }

  private get maxLength(): number | null {
    const input = this.appInputRestrict();
    if (Array.isArray(input) || input === 'unknown') return null;
    return fieldConstraints[input]?.maxLength ?? null;
  }

  @HostBinding('attr.maxlength')
  get maxLengthAttr(): number | null {
    return this.maxLength;
  }

  @HostListener('keypress', ['$event'])
  onKeyPress(event: KeyboardEvent) {
    const el = event.target as HTMLInputElement;
    const selectionLength = (el.selectionEnd ?? 0) - (el.selectionStart ?? 0);

    // length check FIRST (fast fail)
    if (this.maxLength && selectionLength === 0 && el.value.length >= this.maxLength) {
      event.preventDefault();
      return;
    }

    if (!this.pattern.test(event.key)) {
      event.preventDefault();
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: Event) {
    const pattern = this.consecutivePattern;
    if (!pattern) return;

    const el = event.target as HTMLInputElement;
    const fixed = el.value.replace(pattern, (match) => match[0]);
    if (fixed !== el.value) {
      el.value = fixed;
      el.dispatchEvent(new Event('input'));
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent) {
    event.preventDefault();

    const paste = event.clipboardData?.getData('text') ?? '';

    // filter out invalid characters
    const filtered = Array.from(paste)
      .filter(char => this.pattern.test(char))
      .join('');

    // handle consecutive special chars
    const consecutivePattern = this.consecutivePattern;
    const cleaned = consecutivePattern
      ? filtered.replace(consecutivePattern, (match) => match[0])
      : filtered;

    // insert at cursor position
    const el = event.target as HTMLInputElement;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const current = el.value;
    let result = current.substring(0, start) + cleaned + current.substring(end);
    if (this.maxLength) result = result.substring(0, this.maxLength);
    el.value = result;

    // trigger angular to detect the change
    el.dispatchEvent(new Event('input'));
  }
}
